// src/components/Layout/DashboardLayout.js
import React, { useState, useEffect, useContext } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { ThemeContext } from '../../context/ThemeContext';
import { useNotification } from '../../context/NotificationProvider';
import DashboardHome from '../../pages/Dashboard/DashboardHome';
import { 
  LayoutDashboard, ShoppingCart, Users, Store, Settings, LogOut, Menu, X,
  ChevronDown, Bell, FileText, User, Bell as BellIcon, Utensils, Truck,
  CreditCard, Headphones, Globe, MessageSquare, ChevronRight, MapPin,
  FileCheck, HelpCircle, Mail, File, FileSpreadsheet, FileText as FileTextIcon,
  Award, Newspaper, MessageCircle, Database, Layout, Send, Activity,
  Image, DollarSign, FileCog, Shield, BookOpen, FileX, Palette, Eye, EyeOff,
  MoreHorizontal, Wallet, HandCoins, Receipt, Clock, Map, LifeBuoy,
  MonitorCog, Ticket, BellRing, Radio,  MessageCircleQuestion,
  Globe2, Layers, ShieldCheck, FileText as FileText2, Building2,
  Star, FileBarChart, Settings2, MapPin as MapPin2, Bus, DollarSign as DollarSign2,
  Coins, Layers2, Users as Users2, UserCog, UserCheck
} from 'lucide-react';

// Import logo from assets
import logoImage from '../../assets/Zaptodoor New Logo (69).png';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifMenuOpen, setNotifMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showThemeColors, setShowThemeColors] = useState(true);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  
  const { user, role, permissions, hasPermission, logout, isLoading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Theme Context
  const { theme, currentTheme, setCurrentTheme, themeConfig } = useContext(ThemeContext);
  const isDarkMode = currentTheme === 'dark';

  // Notification Context
  const { showSuccess, showError, showWarning, showInfo } = useNotification();

  // --------------------------------------------
  // ✅ FIXED: Combined loading state
  // --------------------------------------------
  useEffect(() => {
    // Check if permissions are already loaded
    const checkPermissions = () => {
      const perms = localStorage.getItem('userPermission') || localStorage.getItem('userPermissions');
      const userData = localStorage.getItem('userData');
      
      if (perms && userData) {
        setIsLoading(false);
        return true;
      }
      return false;
    };

    // If permissions already loaded, set loading false
    if (checkPermissions()) return;

    // Otherwise wait for permissions to load
    if (!permissionsLoading && permissions && permissions.length > 0) {
      setIsLoading(false);
      return;
    }

    // Fallback timeout
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [permissionsLoading, permissions]);

  // --------------------------------------------
  // RESPONSIVE SIDEBAR LOGIC
  // --------------------------------------------
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      
      if (width < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Show welcome notification on load
  useEffect(() => {
    if (!isLoading && user) {
      setTimeout(() => {
        showSuccess('Welcome Back!', `Hello ${getUserName()}, welcome to your dashboard.`, 4000);
      }, 1000);
    }
  }, [isLoading, user]);

  const getUserName = () => {
    // ✅ FIXED: Check both user.admin and user object
    if (user?.admin?.name) return user.admin.name;
    if (user?.name) return user.name;
    
    // ✅ Try to get from localStorage directly
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        if (userData?.admin?.name) return userData.admin.name;
        if (userData?.name) return userData.name;
      }
    } catch (e) {}
    
    return 'User';
  };

  const getUserInitial = () => {
    const name = getUserName();
    return name?.[0]?.toUpperCase() || 'U';
  };

  // ✅ FIXED: hasAccess with better fallback
  const hasAccess = (permission) => {
    // Super Admin has all access
    if (role?.role_name === 'Super Admin' || role?.role_name === 'super admin') return true;
    if (role?.role_name === 'superAdmin') return true;
    
    if (!permission) return false;
    if (Array.isArray(permission)) return permission.some(item => hasAccess(item));
    
    // Check in permissions array
    if (permissions?.includes(permission)) return true;
    
    // ✅ Try to get permissions from localStorage directly
    try {
      const permsStr = localStorage.getItem('userPermissions') || localStorage.getItem('userPermission');
      if (permsStr) {
        let perms = [];
        try {
          perms = JSON.parse(permsStr);
        } catch {
          perms = permsStr.split(',').map(p => p.trim());
        }
        return perms.includes(permission);
      }
    } catch (e) {}
    
    return false;
  };

  const handleLogout = () => {
    showInfo('Logging Out', 'You are being logged out...', 2000);
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 500);
  };

  const isDashboardHome = () => {
    return window.location.pathname === '/dashboard' || window.location.pathname === '/dashboard/';
  };

  const toggleSubMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const isMobile = windowWidth <= 768;
  const showBottomNav = windowWidth <= 1024;

  // Function to get current page name from URL
  const getCurrentPageName = () => {
    const path = location.pathname;
    
    if (path === '/dashboard' || path === '/dashboard/') {
      return 'Dashboard';
    }
    
    const allMenus = [
      { id: 'dashboard', label: 'Dashboard', path: '/dashboard' },
      { id: 'orders', label: 'Orders', path: '/dashboard/orders' },
      { id: 'users', label: 'Users', path: '/dashboard/users' },
      { id: 'restaurants', label: 'Restaurants', path: '/dashboard/restaurants' },
      { id: 'dishes', label: 'Dishes', path: '/dashboard/dishes' },
      { id: 'drivers', label: 'Drivers', path: '/dashboard/drivers' },
      { id: 'payments', label: 'Payments', path: '/dashboard/payments' },
      { id: 'app_support', label: 'App Support', path: '/dashboard/app-support' },
      { id: 'website', label: 'Website', path: '/dashboard/website' },
      { id: 'config', label: 'Config', path: '/dashboard/config' },
      { id: 'reports', label: 'Reports', path: '/dashboard/reports' },
      { id: 'dev_feedback', label: 'Dev Feedback', path: '/dashboard/dev-feedback' },
      { id: 'settings', label: 'Settings', path: '/dashboard/settings' },
      { id: 'profile', label: 'Profile', path: '/dashboard/profile' },
      { id: 'official_member', label: 'Official Member', path: '/dashboard/official-member' },
    ];

    for (const menu of allMenus) {
      if (path === menu.path || path.startsWith(menu.path + '/')) {
        return menu.label;
      }
    }

    const subMenus = [
      { label: 'Order Detail', path: '/dashboard/orders/detail' },
      { label: 'Order Detail Page', path: '/dashboard/order-detail' },
      { label: 'Order Tracking', path: '/dashboard/order-tracking' },
      { label: 'Order History', path: '/dashboard/users/order-history' },
      { label: 'User Edit', path: '/dashboard/users/edit' },
      { label: 'Overview', path: '/dashboard/restaurants/overview' },
      { label: 'Menu', path: '/dashboard/restaurants/menu' },
      { label: 'Orders', path: '/dashboard/restaurants/orders' },
      { label: 'Timings', path: '/dashboard/restaurants/timings' },
      { label: 'Payments', path: '/dashboard/restaurants/payments' },
      { label: 'Reviews', path: '/dashboard/restaurants/reviews' },
      { label: 'Location', path: '/dashboard/restaurants/location' },
      { label: 'Documents', path: '/dashboard/restaurants/documents' },
      { label: 'Active Offers', path: '/dashboard/restaurants/active-offers' },
      { label: 'Restaurant Detail', path: '/dashboard/restaurants/detail' },
      { label: 'Create Restaurant', path: '/dashboard/restaurants/create' },
      { label: 'Edit Restaurant', path: '/dashboard/restaurants/edit' },
      { label: 'Menu', path: '/dashboard/dishes/menu' },
      { label: 'Overview', path: '/dashboard/drivers/overview' },
      { label: 'Delivery History', path: '/dashboard/drivers/delivery-history' },
      { label: 'Edit Driver', path: '/dashboard/drivers/edit-driver' },
      { label: 'Cash Dashboard', path: '/dashboard/drivers/cash-dashboard' },
      { label: 'Create Driver', path: '/dashboard/drivers/create' },
      { label: 'View Driver', path: '/dashboard/drivers/view' },
      { label: 'Withdrawal', path: '/dashboard/payments/withdrawal' },
      { label: 'Online Money', path: '/dashboard/payments/online-money' },
      { label: 'Invoices', path: '/dashboard/payments/invoices' },
      { label: 'Cash Verification', path: '/dashboard/payments/cash-verification' },
      { label: 'Verified History', path: '/dashboard/payments/verified-history' },
      { label: 'Locker to Bank', path: '/dashboard/payments/locker-to-bank' },
      { label: 'Transit Tracker', path: '/dashboard/payments/transit-tracker' },
      { label: 'Bank History', path: '/dashboard/payments/bank-history' },
      { label: 'Map', path: '/dashboard/map' },
      { label: 'Support', path: '/dashboard/app-support/support' },
      { label: 'App Settings', path: '/dashboard/app-support/settings' },
      { label: 'App Banners', path: '/dashboard/app-support/app-banners' },
      { label: 'RestaurantOffer Banner', path: '/dashboard/app-support/restaurant-offer-banner' },
      { label: 'Restaurant LoginBanner', path: '/dashboard/app-support/restaurant-login-banner' },
      { label: 'Locations', path: '/dashboard/app-support/locations' },
      { label: 'Tickets', path: '/dashboard/app-support/tickets' },
      { label: 'Order Complaints', path: '/dashboard/app-support/complaints' },
      { label: 'COD Refund Queue', path: '/dashboard/app-support/cod-refunds' },
      { label: 'Notifications', path: '/dashboard/app-support/notifications' },
      { label: 'Send Notifications', path: '/dashboard/app-support/send-notifications' },
      { label: 'Live Status', path: '/dashboard/app-support/live-status' },
      { label: 'FAQ', path: '/dashboard/app-support/faq' },
      { label: 'Web Banners', path: '/dashboard/website/banners' },
      { label: 'Pages', path: '/dashboard/website/pages' },
      { label: 'Privacy Policy', path: '/dashboard/website/privacy-policy' },
      { label: 'Terms & Conditions', path: '/dashboard/website/terms-conditions' },
      { label: 'About Us', path: '/dashboard/website/about-us' },
      { label: 'Cancellation Policy', path: '/dashboard/website/cancellation-policy' },
      { label: 'Refund Policy', path: '/dashboard/website/refund-policy' },
      { label: 'Services', path: '/dashboard/website/services' },
      { label: 'Why Choose Us', path: '/dashboard/website/choose-us' },
      { label: 'Press Release', path: '/dashboard/website/press-release' },
      { label: 'Enquiries', path: '/dashboard/website/enquiries' },
      { label: 'Footer Certificates', path: '/dashboard/website/footer-certificates' },
      { label: 'Web Setting', path: '/dashboard/website/web-setting' },
      { label: 'Delivery Fare', path: '/dashboard/config/delivery-fare' },
      { label: 'Platform Fee', path: '/dashboard/config/platform-fee' },
      { label: 'Application Pages', path: '/dashboard/config/application-pages' },
      { label: 'View Reports', path: '/dashboard/reports/view' },
      { label: 'Regions', path: '/dashboard/settings/regions' },
      { label: 'Staff', path: '/dashboard/settings/staff' },
      { label: 'Roles', path: '/dashboard/settings/roles' },
      { label: 'General', path: '/dashboard/settings/general' },
    ];

    for (const sub of subMenus) {
      if (path === sub.path) {
        return sub.label;
      }
    }

    const segments = path.split('/').filter(s => s);
    if (segments.length > 1) {
      const lastSegment = segments[segments.length - 1];
      return lastSegment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return 'Admin Panel';
  };

  const currentPageName = getCurrentPageName();

  // Dynamic theme styles
  const themeStyles = {
    sidebar: {
      background: theme.surface,
      borderRight: `1px solid ${theme.border}`,
    },
    sidebarHover: theme.surfaceHover || theme.primaryLight,
    navText: isDarkMode ? 'rgba(255,255,255,0.7)' : theme.textSecondary,
    navTextHover: theme.primary,
    navActiveBg: theme.primaryLight,
    navBorder: `3px solid ${theme.primary}`,
    userMenuBg: theme.surface,
    userMenuBorder: `1px solid ${theme.border}`,
    mainBg: theme.background,
    headerBg: theme.surface,
    headerBorder: `1px solid ${theme.border}`,
    headerText: theme.textPrimary,
    headerSubText: theme.textSecondary,
    roleBadgeBg: theme.primaryGradient || `linear-gradient(135deg, ${theme.primary}, ${theme.primary}dd)`,
    divider: theme.border,
    logoGradient: theme.primaryGradient || `linear-gradient(135deg, ${theme.primary}, ${theme.primary}dd)`,
  };

  // Sidebar widths
  const SIDEBAR_OPEN = windowWidth < 1024 ? '240px' : '260px';
  const SIDEBAR_CLOSED = '72px';

  // --------------------------------------------
  // MENU DEFINITIONS
  // --------------------------------------------
  const dashboardMenu = { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: <LayoutDashboard size={18} />, 
    path: '/dashboard', 
    rootPermission: 'dashboard', 
    isSubMenu: false 
  };
  
  const ordersMenu = { 
    id: 'orders', 
    label: 'Orders', 
    icon: <ShoppingCart size={18} />, 
    path: '/dashboard/orders', 
    rootPermission: 'orders', 
    isSubMenu: false 
  };
  
  const usersMenu = { 
    id: 'users', 
    label: 'Users', 
    icon: <Users size={18} />, 
    path: '/dashboard/users', 
    rootPermission: 'users', 
    isSubMenu: false 
  };
  
  const restaurantsMenu = { 
    id: 'restaurants', 
    label: 'Restaurants', 
    icon: <Store size={18} />, 
    path: '/dashboard/restaurants', 
    rootPermission: 'restaurants', 
    isSubMenu: false
  };
  
  const dishesMenu = { 
    id: 'dishes', 
    label: 'Menu', 
    icon: <Utensils size={18} />, 
    path: '/dashboard/dishes/menu', 
    rootPermission: 'dishes.menu', 
    isSubMenu: false,
    subMenus: [
      { id: 'dishes_menu', label: 'Menu', path: '/dashboard/dishes/menu', permission: 'dishes.menu', icon: <Utensils size={16} /> },
    ]
  };
  
  const driversMenu = { 
    id: 'drivers', 
    label: 'Drivers', 
    icon: <Truck size={18} />, 
    path: '/dashboard/drivers', 
    rootPermission: 'drivers', 
    isSubMenu: false
  };
  
  const paymentsMenu = {
    id: 'payments', 
    label: 'Payments', 
    icon: <CreditCard size={18} />, 
    path: '/dashboard/payments', 
    rootPermission: 'payments', 
    isSubMenu: true,
    subMenus: [
      { id: 'payments_online_money', label: 'Online Money', path: '/dashboard/payments/online-money', permission: 'payments', icon: <Coins size={16} /> },
      { id: 'payments_withdrawal', label: 'Withdrawal', path: '/dashboard/payments/withdrawal', permission: 'payments.withdrawal', icon: <Wallet size={16} /> },
      { id: 'payments_invoices', label: 'Invoices', path: '/dashboard/payments/invoices', permission: 'payments.invoices', icon: <Receipt size={16} /> },
      { id: 'payments_cash_verification', label: 'Cash Verification', path: '/dashboard/payments/cash-verification/pending', permission: 'payments.Cash Verification', icon: <HandCoins size={16} /> },
      { id: 'payments_verified_history', label: 'Verified History', path: '/dashboard/payments/cash-verification/verified', permission: 'payments.Verified History', icon: <FileCheck size={16} /> },
      { id: 'payments_cashmoney', label: 'CashMoney', path: '/dashboard/payments/cashmoney', permission: 'payments', icon: <HandCoins size={16} /> },
      { id: 'payments_locker_to_bank', label: 'Locker to Bank', path: '/dashboard/payments/locker-to-bank', permission: 'payments.Locker to Bank', icon: <Building2 size={16} /> },
      { id: 'payments_transit_tracker', label: 'Transit Tracker', path: '/dashboard/payments/transit-tracker', permission: 'payments.Transit Tracker', icon: <Bus size={16} /> },
      { id: 'payments_bank_history', label: 'Bank History', path: '/dashboard/payments/bank-history', permission: ['payments.Bank History', 'payments.Verified History'], icon: <Building2 size={16} /> },
    ]
  };
const appSupportMenu = {
  id: 'app_support', 
  label: 'App Support', 
  icon: <Headphones size={18} />, 
  path: '/dashboard/app-support', 
  rootPermission: 'app_support', 
  isSubMenu: true,
  subMenus: [
    { id: 'app_support_map', label: 'Map', path: '/dashboard/app-support/map', permission: 'app_support.map', icon: <Map size={16} /> },
    { id: 'app_support_support', label: 'Support', path: '/dashboard/app-support/support', permission: 'app_support.support', icon: <LifeBuoy size={16} /> },
    { id: 'app_support_settings', label: 'AppSettings', path: '/dashboard/app-support/settings', permission: 'app_support.app_settings', icon: <MonitorCog size={16} /> },
    // FIX: Change these permission checks to match your actual permissions
    { id: 'app_support_app_banners', label: 'App Banners', path: '/dashboard/app-support/app-banners', permission: 'app_support.banners', icon: <Image size={16} /> },
    { id: 'app_support_restaurant_offer_banner', label: 'RestaurantOffer Banner', path: '/dashboard/app-support/restaurant-offer-banner', permission: 'app_support.banners', icon: <Image size={16} /> },
    { id: 'app_support_restaurant_login_banner', label: 'Restaurant LoginBanner', path: '/dashboard/app-support/restaurant-login-banner', permission: 'app_support.banners', icon: <Image size={16} /> },
    { id: 'app_support_locations', label: 'Locations', path: '/dashboard/app-support/locations', permission: 'app_support.locations', icon: <MapPin2 size={16} /> },
    { id: 'app_support_tickets', label: 'Tickets', path: '/dashboard/app-support/tickets', permission: 'app_support.tickets', icon: <Ticket size={16} /> },
    { id: 'app_support_complaints', label: 'Order Complaints', path: '/dashboard/app-support/complaints', permission: 'app_support.tickets', icon: <Ticket size={16} /> },
    { id: 'app_support_cod_refunds', label: 'COD Refund Queue', path: '/dashboard/app-support/cod-refunds', permission: 'app_support.tickets', icon: <Ticket size={16} /> },
    { id: 'app_support_notifications', label: 'Notifications', path: '/dashboard/app-support/notifications', permission: 'app_support.notifications', icon: <BellRing size={16} /> },
    { id: 'app_support_send', label: 'Send Notifications', path: '/dashboard/app-support/send-notifications', permission: 'app_support.send_notifications', icon: <Send size={16} /> },
    { id: 'app_support_live', label: 'Live Status', path: '/dashboard/app-support/live-status', permission: 'app_support.live_status', icon: <Radio size={16} /> },
    { id: 'app_support_faq', label: 'FAQ', path: '/dashboard/app-support/faq', permission: 'app_support.faq', icon: <MessageCircleQuestion size={16} /> },
  ]
};

  const websiteMenu = {
    id: 'website', 
    label: 'Website', 
    icon: <Globe size={18} />, 
    path: '/dashboard/website', 
    rootPermission: 'website.website', 
    isSubMenu: true,
    subMenus: [
      { id: 'website_banners', label: 'Web Banners', path: '/dashboard/website/banners', permission: 'website.web_banners', icon: <Globe2 size={16} /> },
      { id: 'website_pages', label: 'Pages', path: '/dashboard/website/pages', permission: 'website.pages', icon: <Layers size={16} /> },
      { id: 'website_privacy', label: 'Privacy Policy', path: '/dashboard/website/privacy-policy', permission: 'website.privacy_policy', icon: <ShieldCheck size={16} /> },
      { id: 'website_terms', label: 'Terms & Conditions', path: '/dashboard/website/terms-conditions', permission: 'website.terms_conditions', icon: <FileText2 size={16} /> },
      { id: 'website_about', label: 'About Us', path: '/dashboard/website/about-us', permission: 'website.about_us', icon: <Building2 size={16} /> },
      { id: 'website_cancellation', label: 'Cancellation Policy', path: '/dashboard/website/cancellation-policy', permission: 'website.cancellation_policy', icon: <FileX size={16} /> },
      { id: 'website_refund', label: 'Refund Policy', path: '/dashboard/website/refund-policy', permission: 'website.refund_policy', icon: <Coins size={16} /> },
      { id: 'website_services', label: 'Services', path: '/dashboard/website/services', permission: 'website.services', icon: <Layers2 size={16} /> },
      { id: 'website_choose', label: 'Why Choose Us', path: '/dashboard/website/choose-us', permission: 'website.choose_us', icon: <Star size={16} /> },
      { id: 'website_press', label: 'Press Release', path: '/dashboard/website/press-release', permission: 'website.press_release', icon: <Newspaper size={16} /> },
      { id: 'website_enquiries', label: 'Enquiries', path: '/dashboard/website/enquiries', permission: 'website.enquiries', icon: <Mail size={16} /> },
      { id: 'website_footer', label: 'Footer Certificates', path: '/dashboard/website/footer-certificates', permission: 'website.footer_certificates', icon: <Award size={16} /> },
      { id: 'website_setting', label: 'Web Setting', path: '/dashboard/website/web-setting', permission: 'website.web_setting', icon: <Settings2 size={16} /> },
    ]
  };

  const configMenu = {
    id: 'config', 
    label: 'Config', 
    icon: <Settings size={18} />, 
    path: '/dashboard/config', 
    rootPermission: 'config', 
    isSubMenu: true,
    subMenus: [
      { id: 'config_delivery_fare', label: 'Delivery Fare', path: '/dashboard/config/delivery-fare', permission: 'config.delivery_fare', icon: <DollarSign size={16} /> },
      { id: 'config_platform_fee', label: 'Platform Fee', path: '/dashboard/config/platform-fee', permission: 'config.platform_fee', icon: <DollarSign size={16} /> },
      { id: 'config_application_pages', label: 'Application Pages', path: '/dashboard/config/application-pages', permission: 'config.application_pages', icon: <FileCog size={16} /> },
    ]
  };

  const reportsMenu = {
    id: 'reports', 
    label: 'Reports', 
    icon: <FileBarChart size={18} />, 
    path: '/dashboard/reports', 
    rootPermission: 'reports', 
    isSubMenu: true,
    subMenus: [
      { id: 'reports_view', label: 'View Reports', path: '/dashboard/reports/view', permission: 'reports.view', icon: <FileTextIcon size={16} /> },
    ]
  };

  const devFeedbackMenu = { 
    id: 'dev_feedback', 
    label: 'Dev Feedback', 
    icon: <MessageSquare size={18} />, 
    path: '/dashboard/dev-feedback', 
    rootPermission: 'dev_feedback.dev_feedback', 
    isSubMenu: false 
  };

  const settingsMenu = {
    id: 'settings',
    label: 'Settings',
    icon: <Settings2 size={18} />,
    path: '/dashboard/settings',
    rootPermission: 'settings.regions',
    isSubMenu: true,
    subMenus: [
      { id: 'settings_regions', label: 'Regions', path: '/dashboard/settings/regions', permission: 'settings.regions', icon: <MapPin2 size={16} /> },
      { id: 'settings_staff', label: 'Staff', path: '/dashboard/settings/staff', permission: 'settings.staff', icon: <Users2 size={16} /> },
      { id: 'settings_roles', label: 'Roles', path: '/dashboard/settings/roles', permission: 'settings.roles', icon: <UserCheck size={16} /> },
      { id: 'settings_general', label: 'General', path: '/dashboard/settings/general', permission: 'settings.general', icon: <Settings2 size={16} /> },
    ]
  };

  const officialMemberMenu = {
    id: 'official_member',
    label: 'Official Member',
    icon: <Award size={18} />,
    path: '/dashboard/official-member',
    rootPermission: 'official_member',
    isSubMenu: false
  };

  const allMenus = [
    dashboardMenu, ordersMenu, usersMenu, restaurantsMenu,
    dishesMenu, driversMenu, paymentsMenu, appSupportMenu,
    websiteMenu, configMenu, reportsMenu, devFeedbackMenu,
    settingsMenu, officialMemberMenu
  ];

  // ✅ FIXED: getVisibleMenus with proper loading check
  const getVisibleMenus = () => {
    // If still loading, return empty array to avoid rendering issues
    if (isLoading || permissionsLoading) return [];
    // Official Member is an admin utility page and does not require a role
    // permission entry, so it must remain visible in the sidebar.
    return allMenus.filter(menu =>
      menu.id === 'official_member' || hasAccess(menu.rootPermission)
    );
  };
  
  const getVisibleSubMenus = (menu) => {
    if (!menu.isSubMenu) return [];
    if (isLoading || permissionsLoading) return [];
    return menu.subMenus?.filter(sub => hasAccess(sub.permission)) || [];
  };

  const visibleMenus = getVisibleMenus();

  // --------------------------------------------
  // BOTTOM NAVIGATION
  // --------------------------------------------
  const bottomNavItems = [
    { id: 'dashboard', label: 'Home', icon: <LayoutDashboard size={20} />, path: '/dashboard' },
    { id: 'orders', label: 'Orders', icon: <ShoppingCart size={20} />, path: '/dashboard/orders' },
    { id: 'restaurants', label: 'Restaurants', icon: <Store size={20} />, path: '/dashboard/restaurants' },
    { id: 'drivers', label: 'Drivers', icon: <Truck size={20} />, path: '/dashboard/drivers' },
  ];

  const visibleBottomNav = bottomNavItems.filter(item => hasAccess(item.id));

  const moreItems = visibleMenus.filter(menu => 
    !bottomNavItems.some(bottom => bottom.id === menu.id)
  );

  const handleBottomNav = (path) => {
    navigate(path);
    setMoreMenuOpen(false);
  };

  // --------------------------------------------
  // LOADING
  // --------------------------------------------
  if (isLoading || permissionsLoading) {
    return (
      <div style={{ 
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh',
        background: theme.background
      }}>
        <div style={{ 
          width: '40px', height: '40px', 
          border: `3px solid ${theme.border}`,
          borderTop: `3px solid ${theme.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // --------------------------------------------
  // RENDER
  // --------------------------------------------
  return (
    <>
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        body { margin: 0; overflow: hidden; }
        
        .submenu-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .submenu-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .submenu-scroll::-webkit-scrollbar-thumb {
          background: ${theme.primary}40;
          border-radius: 4px;
        }
        .submenu-scroll::-webkit-scrollbar-thumb:hover {
          background: ${theme.primary};
        }
      `}</style>

      <div style={{ 
        display: 'flex', height: '100vh', background: themeStyles.mainBg,
        transition: 'background 0.3s ease', overflow: 'hidden', width: '100%',
      }}>
        {/* SIDEBAR */}
        <aside style={{
          position: 'fixed', top: 0, left: 0, bottom: 0,
          width: sidebarOpen ? SIDEBAR_OPEN : SIDEBAR_CLOSED,
          background: themeStyles.sidebar.background,
          color: isDarkMode ? '#fff' : theme.textPrimary,
          transition: 'width 0.3s ease, background 0.3s ease',
          overflow: 'hidden', zIndex: 1000, display: 'flex', flexDirection: 'column',
          boxShadow: isDarkMode ? '2px 0 15px rgba(0,0,0,0.5)' : `2px 0 15px ${theme.primaryLight}`,
          borderRight: themeStyles.sidebar.borderRight, flexShrink: 0,
          ...(isMobile && !sidebarOpen && { transform: 'translateX(-100%)' }),
        }}>
          {/* Logo & Heading */}
          <div style={{
            padding: sidebarOpen ? '20px 16px' : '16px 12px', 
            borderBottom: `1px solid ${themeStyles.divider}`,
            display: 'flex', 
            flexDirection: sidebarOpen ? 'row' : 'column', 
            alignItems: 'center', 
            justifyContent: sidebarOpen ? 'flex-start' : 'center',
            gap: '12px', 
            minHeight: '72px',
          }}>
            <div style={{
              width: sidebarOpen ? '44px' : '36px', 
              height: sidebarOpen ? '44px' : '36px', 
              flexShrink: 0,
              borderRadius: '10px',
              overflow: 'hidden',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img 
                src={logoImage} 
                alt="Zaptodoor Logo" 
                style={{
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'contain', 
                  imageRendering: 'crisp-edges',
                  WebkitImageRendering: '-webkit-optimize-contrast',
                }}
                draggable={false}
              />
            </div>

            {sidebarOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.1 }}>
                <span style={{ 
                  fontSize: '22px', fontWeight: 800, 
                  background: themeStyles.logoGradient,
                  WebkitBackgroundClip: 'text', 
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.5px',
                }}>
                  Zaptodoor
                </span>
                <span style={{ 
                  fontSize: '10px', fontWeight: 700, 
                  color: theme.textMuted, 
                  letterSpacing: '1.5px', 
                  textTransform: 'uppercase',
                  marginTop: '3px'
                }}>
                  Admin Panel
                </span>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }} className="no-scrollbar">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {visibleMenus.length > 0 ? (
                visibleMenus.map((menu) => {
                  const visibleSubMenus = getVisibleSubMenus(menu);
                  const isSubMenuActive = visibleSubMenus.some(sub => window.location.pathname === sub.path);
                  const isExpanded = expandedMenus[menu.id];

                  return (
                    <li key={menu.id}>
                      {menu.isSubMenu ? (
                        <>
                          <div
                            onClick={() => sidebarOpen && toggleSubMenu(menu.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: sidebarOpen ? '12px' : '0',
                              padding: sidebarOpen ? '10px 16px' : '10px 0',
                              color: isSubMenuActive ? theme.primary : themeStyles.navText,
                              backgroundColor: isSubMenuActive ? themeStyles.navActiveBg : 'transparent',
                              transition: 'all 0.2s ease',
                              borderLeft: isSubMenuActive ? themeStyles.navBorder : '3px solid transparent',
                              fontSize: '13px', fontWeight: isSubMenuActive ? 600 : 500,
                              justifyContent: sidebarOpen ? 'flex-start' : 'center',
                              whiteSpace: 'nowrap', cursor: sidebarOpen ? 'pointer' : 'default',
                              position: 'relative',
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubMenuActive && sidebarOpen) {
                                e.currentTarget.style.color = themeStyles.navTextHover;
                                e.currentTarget.style.background = themeStyles.sidebarHover;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubMenuActive && sidebarOpen) {
                                e.currentTarget.style.color = themeStyles.navText;
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {menu.icon}
                            </span>
                            {sidebarOpen && (
                              <>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{menu.label}</span>
                                <ChevronRight size={14} style={{
                                  transition: 'transform 0.3s ease',
                                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                  flexShrink: 0, opacity: 0.5,
                                }} />
                              </>
                            )}
                          </div>

                          {sidebarOpen && isExpanded && visibleSubMenus.length > 0 && (
                            <ul className="submenu-scroll" style={{
                              listStyle: 'none', padding: '4px 0', margin: 0,
                              background: isDarkMode ? 'rgba(0,0,0,0.2)' : theme.primaryLight,
                              maxHeight: '280px',
                              overflowY: 'auto',
                              transition: 'all 0.3s ease',
                            }}>
                              {visibleSubMenus.map((sub) => (
                                <li key={sub.id}>
                                  <NavLink
                                    to={sub.path}
                                    style={({ isActive }) => ({
                                      display: 'flex', alignItems: 'center', gap: '10px',
                                      padding: '8px 16px 8px 48px',
                                      color: isActive ? theme.primary : themeStyles.navText,
                                      backgroundColor: isActive ? theme.surface : 'transparent',
                                      textDecoration: 'none', transition: 'all 0.2s ease',
                                      borderLeft: isActive ? themeStyles.navBorder : '3px solid transparent',
                                      fontSize: '12px', fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap',
                                    })}
                                    onMouseEnter={(e) => {
                                      if (!e.currentTarget.classList.contains('active')) {
                                        e.currentTarget.style.color = themeStyles.navTextHover;
                                        e.currentTarget.style.background = themeStyles.sidebarHover;
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!e.currentTarget.classList.contains('active')) {
                                        e.currentTarget.style.color = themeStyles.navText;
                                        e.currentTarget.style.background = 'transparent';
                                      }
                                    }}
                                  >
                                    <span style={{ opacity: 0.6 }}>{sub.icon || <FileText size={14} />}</span>
                                    <span>{sub.label}</span>
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : (
                        <NavLink
                          to={menu.path}
                          end={menu.id === 'dashboard'}
                          style={({ isActive }) => ({
                            display: 'flex', alignItems: 'center', gap: sidebarOpen ? '12px' : '0',
                            padding: sidebarOpen ? '10px 16px' : '10px 0',
                            color: isActive ? theme.primary : themeStyles.navText,
                            backgroundColor: isActive ? themeStyles.navActiveBg : 'transparent',
                            textDecoration: 'none', transition: 'all 0.2s ease',
                            borderLeft: isActive ? themeStyles.navBorder : '3px solid transparent',
                            fontSize: '13px', fontWeight: isActive ? 600 : 500,
                            justifyContent: sidebarOpen ? 'flex-start' : 'center', whiteSpace: 'nowrap',
                            position: 'relative',
                          })}
                          onMouseEnter={(e) => {
                            if (!e.currentTarget.classList.contains('active')) {
                              e.currentTarget.style.color = themeStyles.navTextHover;
                              e.currentTarget.style.background = themeStyles.sidebarHover;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!e.currentTarget.classList.contains('active')) {
                              e.currentTarget.style.color = themeStyles.navText;
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {menu.icon}
                          </span>
                          {sidebarOpen && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{menu.label}</span>}
                        </NavLink>
                      )}
                    </li>
                  );
                })
              ) : (
                <li style={{ padding: '16px', textAlign: 'center', color: themeStyles.navText, fontSize: '11px' }}>
                  No permissions available
                </li>
              )}
            </ul>
          </nav>

          {/* Theme Selector */}
          <div style={{
            borderTop: `1px solid ${themeStyles.divider}`,
            padding: sidebarOpen ? '12px' : '8px',
            display: 'flex', 
            flexDirection: 'column',
            alignItems: sidebarOpen ? 'flex-start' : 'center',
          }}>
            {sidebarOpen && (
              <button
                onClick={() => setShowThemeColors(!showThemeColors)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: theme.textPrimary,
                  borderRadius: '6px',
                  transition: 'all 0.2s',
                  marginBottom: showThemeColors ? '8px' : '0',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = themeStyles.sidebarHover}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Palette size={15} color={theme.primary} />
                  <span style={{
                    fontSize: '10px', fontWeight: 700,
                    color: theme.textPrimary,
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>
                    Theme Color
                  </span>
                </div>
                {showThemeColors ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            )}
            
            {showThemeColors && (
              <div style={{
                display: 'flex', 
                gap: sidebarOpen ? '10px' : '8px',
                flexWrap: 'wrap',
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
                width: '100%',
                padding: sidebarOpen ? '0 4px' : '0',
                transition: 'all 0.3s ease',
              }}>
                {Object.keys(themeConfig || {}).map((colorKey) => {
                  const isActive = currentTheme === colorKey;
                  const themeData = themeConfig[colorKey];
                  return (
                    <button
                      key={colorKey}
                      onClick={() => setCurrentTheme(colorKey)}
                      title={`${themeData?.name || colorKey} Theme`}
                      style={{
                        width: sidebarOpen ? '32px' : '28px',
                        height: sidebarOpen ? '32px' : '28px',
                        borderRadius: '50%',
                        background: themeData?.primaryGradient || theme.primaryGradient,
                        border: isActive ? '3px solid #ffffff' : '3px solid transparent',
                        boxShadow: isActive 
                          ? `0 0 0 2px ${themeData?.primary || theme.primary}, 0 4px 12px ${themeData?.primary}40` 
                          : '0 2px 8px rgba(0,0,0,0.15)',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                        flexShrink: 0,
                        position: 'relative',
                        outline: 'none',
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.transform = 'scale(1.15)'; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      {isActive && (
                        <div style={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          color: '#fff', fontSize: sidebarOpen ? '14px' : '12px', fontWeight: 'bold',
                          textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                        }}>
                          ✓
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            
            {sidebarOpen && showThemeColors && (
              <div style={{
                fontSize: '11px', color: theme.textMuted, textAlign: 'center',
                width: '100%', background: theme.surfaceAlt, padding: '4px 8px',
                borderRadius: '6px', border: `1px solid ${theme.border}`,
                marginTop: '8px',
                transition: 'all 0.3s ease',
              }}>
                Active: <span style={{ color: theme.primary, fontWeight: 700 }}>{theme.name || 'Default'}</span>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div style={{ borderTop: `1px solid ${themeStyles.divider}`, padding: '12px 16px', position: 'relative' }}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                background: 'none', border: 'none', color: isDarkMode ? '#fff' : theme.textPrimary,
                cursor: 'pointer', padding: '8px', borderRadius: '8px', transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = themeStyles.sidebarHover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: themeStyles.logoGradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700, color: '#fff', flexShrink: 0,
                boxShadow: `0 2px 6px ${theme.primaryLight}`,
              }}>
                {getUserInitial()}
              </div>
              {sidebarOpen && (
                <div style={{ flex: 1, textAlign: 'left', overflow: 'hidden' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isDarkMode ? '#f1f5f9' : theme.textPrimary }}>
                    {getUserName()}
                  </div>
                  <div style={{ fontSize: '10px', color: isDarkMode ? '#94a3b8' : theme.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {role?.role_name || 'No Role'}
                  </div>
                </div>
              )}
              {sidebarOpen && (
                <ChevronDown size={14} style={{
                  transition: 'transform 0.2s', transform: userMenuOpen ? 'rotate(180deg)' : 'rotate(0)',
                  flexShrink: 0, color: isDarkMode ? '#94a3b8' : theme.textSecondary,
                }} />
              )}
            </button>

            {userMenuOpen && sidebarOpen && (
              <div style={{
                position: 'absolute', bottom: '60px', left: '16px', right: '16px',
                background: themeStyles.userMenuBg, borderRadius: '8px', padding: '8px',
                boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.6)' : `0 4px 20px ${theme.primaryLight}`,
                border: themeStyles.userMenuBorder, zIndex: 1001,
              }}>
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                    padding: '8px 10px', background: 'none', border: 'none',
                    color: theme.error || '#ef4444', cursor: 'pointer', borderRadius: '6px',
                    fontSize: '12px', fontWeight: 600, transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = `${theme.error || '#ef4444'}15`}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Mobile Overlay */}
        {isMobile && sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', zIndex: 999,
            }}
          />
        )}

        {/* MAIN CONTENT */}
        <div style={{
          marginLeft: sidebarOpen ? SIDEBAR_OPEN : SIDEBAR_CLOSED,
          flex: 1, display: 'flex', flexDirection: 'column', height: '100vh',
          transition: 'margin-left 0.3s ease', background: themeStyles.mainBg,
          width: `calc(100% - ${sidebarOpen ? SIDEBAR_OPEN : SIDEBAR_CLOSED})`,
          overflow: 'hidden', ...(isMobile && { marginLeft: 0, width: '100%' }),
        }}>
          {/* Header */}
          <header style={{
            flexShrink: 0, background: themeStyles.headerBg, padding: '12px 24px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: themeStyles.headerBorder, transition: 'all 0.3s ease', gap: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  background: themeStyles.sidebarHover, border: '1px solid transparent',
                  cursor: 'pointer', padding: '6px', borderRadius: '6px',
                  color: theme.primary, display: 'flex', alignItems: 'center', transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.primary}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'transparent'}
              >
                {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <div>
                <h1 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 700, margin: 0, color: themeStyles.headerText }}>
                  {currentPageName}
                </h1>
                <p style={{ fontSize: '11px', margin: 0, color: themeStyles.headerSubText }}>
                  Welcome back, {getUserName()}!
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => {
                    setNotifMenuOpen(!notifMenuOpen);
                    showInfo('Notifications', 'You have 3 new notifications!', 3000);
                    setTimeout(() => {
                      navigate('/dashboard/show-notification');
                    }, 500);
                  }}
                  style={{
                    background: themeStyles.sidebarHover, 
                    border: `1px solid ${themeStyles.divider}`,
                    borderRadius: '8px', 
                    padding: '6px', 
                    cursor: 'pointer',
                    display: 'flex', 
                    alignItems: 'center', 
                    color: theme.primary,
                    transition: 'all 0.2s ease', 
                    position: 'relative',
                  }}
                >
                  <BellIcon size={18} />
                  <span style={{
                    position: 'absolute', 
                    top: '-4px', 
                    right: '-4px',
                    background: theme.primary, 
                    color: '#fff', 
                    borderRadius: '50%',
                    width: '16px', 
                    height: '16px', 
                    fontSize: '9px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 700, 
                    boxShadow: `0 2px 4px ${theme.primaryLight}`,
                  }}>
                    3
                  </span>
                </button>

                {/* Notification Dropdown */}
                {notifMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: '380px',
                    maxHeight: '450px',
                    background: theme.surface,
                    borderRadius: '12px',
                    boxShadow: isDarkMode 
                      ? '0 10px 40px rgba(0,0,0,0.6)' 
                      : '0 10px 40px rgba(0,0,0,0.15)',
                    border: `1px solid ${theme.border}`,
                    zIndex: 1000,
                    overflow: 'hidden',
                    animation: 'slideDown 0.3s ease',
                  }}>
                    <div style={{
                      padding: '16px 20px',
                      borderBottom: `1px solid ${theme.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: theme.textPrimary }}>
                          Notifications
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: theme.textMuted }}>
                          You have 3 unread notifications
                        </p>
                      </div>
                      <button
                        onClick={() => setNotifMenuOpen(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: theme.textMuted,
                          padding: '4px',
                          borderRadius: '6px',
                        }}
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <div style={{ padding: '8px 0', maxHeight: '280px', overflowY: 'auto' }}>
                      <div style={{
                        padding: '12px 20px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        borderLeft: `3px solid ${theme.primary}`,
                        background: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: `${theme.primary}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <BellRing size={18} color={theme.primary} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: theme.textPrimary }}>New Order Received</span>
                            <span style={{ fontSize: '10px', color: theme.textMuted }}>2 min ago</span>
                          </div>
                          <p style={{ margin: 0, fontSize: '12px', color: theme.textSecondary }}>Order #12345 from Restaurant XYZ has been placed.</p>
                        </div>
                      </div>
                    </div>

                    <div style={{
                      padding: '12px 20px',
                      borderTop: `1px solid ${theme.border}`,
                      textAlign: 'center',
                    }}>
                      <button
                        onClick={() => {
                          setNotifMenuOpen(false);
                          navigate('/dashboard/show-notification');
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: theme.primary,
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        View All Notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <span style={{
                padding: '4px 12px', 
                background: themeStyles.roleBadgeBg, 
                color: '#fff',
                borderRadius: '20px', 
                fontSize: '10px', 
                fontWeight: 700, 
                whiteSpace: 'nowrap',
                boxShadow: `0 2px 6px ${theme.primaryLight}`, 
                display: isMobile ? 'none' : 'inline',
              }}>
                {role?.role_name || 'No Role'}
              </span>

              <button
                onClick={() => navigate('/dashboard/profile')}
                style={{
                  background: themeStyles.sidebarHover, 
                  border: `1px solid ${themeStyles.divider}`,
                  borderRadius: '8px', 
                  padding: '6px 12px', 
                  cursor: 'pointer',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px', 
                  color: theme.primary,
                  transition: 'all 0.2s ease', 
                  fontSize: '12px', 
                  fontWeight: 600,
                }}
              >
                <User size={16} />
                <span style={{ display: window.innerWidth < 600 ? 'none' : 'inline' }}>Profile</span>
              </button>
            </div>
          </header>

          {/* Page Content */}
          <main style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden', 
            padding: isMobile ? '16px' : '20px 24px',
            paddingBottom: showBottomNav ? '80px' : '20px',
            width: '100%', boxSizing: 'border-box',
          }} className="no-scrollbar">
            {isDashboardHome() ? (
              <DashboardHome isDark={isDarkMode} />
            ) : (
              <Outlet />
            )}
          </main>

          {/* BOTTOM NAVIGATION */}
          {showBottomNav && visibleBottomNav.length > 0 && (
            <div style={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              background: theme.surface,
              borderTop: `1px solid ${theme.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-around',
              padding: '6px 0 8px 0',
              zIndex: 999,
              boxShadow: isDarkMode ? '0 -2px 10px rgba(0,0,0,0.5)' : '0 -2px 10px rgba(0,0,0,0.08)',
            }}>
              {visibleBottomNav.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleBottomNav(item.path)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '4px 8px', flex: 1,
                      color: isActive ? theme.primary : (isDarkMode ? '#94a3b8' : '#64748b'),
                      transition: 'all 0.2s ease',
                      position: 'relative',
                    }}
                  >
                    <span style={{ fontSize: '20px' }}>{item.icon}</span>
                    <span style={{ fontSize: '10px', fontWeight: isActive ? 600 : 400, marginTop: '2px' }}>
                      {item.label}
                    </span>
                    {isActive && (
                      <span style={{
                        position: 'absolute', top: 0, left: '30%', right: '30%',
                        height: '3px', background: theme.primary,
                        borderRadius: '0 0 4px 4px',
                      }} />
                    )}
                  </button>
                );
              })}

              {/* More button */}
              {moreItems.length > 0 && (
                <div style={{ position: 'relative', flex: 1 }}>
                  <button
                    onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '4px 8px', width: '100%',
                      color: moreMenuOpen ? theme.primary : (isDarkMode ? '#94a3b8' : '#64748b'),
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <MoreHorizontal size={22} />
                    <span style={{ fontSize: '10px', fontWeight: moreMenuOpen ? 600 : 400, marginTop: '2px' }}>
                      More
                    </span>
                  </button>

                  {/* More dropdown */}
                  {moreMenuOpen && (
                    <div style={{
                      position: 'absolute', bottom: '56px', left: '50%', transform: 'translateX(-50%)',
                      background: theme.surface,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '12px',
                      boxShadow: isDarkMode ? '0 4px 20px rgba(0,0,0,0.6)' : '0 4px 20px rgba(0,0,0,0.15)',
                      padding: '8px 0',
                      minWidth: '160px',
                      zIndex: 1000,
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}>
                      {moreItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            handleBottomNav(item.path);
                            setMoreMenuOpen(false);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '8px 16px', width: '100%',
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: isDarkMode ? '#e2e8f0' : '#1e293b',
                            fontSize: '13px', fontWeight: 500,
                            transition: 'background 0.2s',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme.primaryLight}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <span style={{ color: theme.primary }}>{item.icon}</span>
                          <span>{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardLayout;
